import React, { useState, useEffect } from 'react';
import {
  Building2, Search, Plus, Clock, CheckCircle, XCircle,
  MapPin, Phone, LogOut, X, Loader
} from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, getCurrentUser } from '../utils/api';

const WorkingBranches = () => {
  const { print_lang } = useLanguage();
  const [myBranches, setMyBranches] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const user = getCurrentUser();

  useEffect(() => {
    loadMyBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMyBranches = async () => {
    try {
      const response = await fetchWithAuth(`/api/dentist-work/dentist/${user?.dentist_id}?include_pending=true`);
      if (response.success) {
        setMyBranches(response.data);
      }
    } catch (error) {
      console.error('Failed to load branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const response = await fetchWithAuth(`/api/dentist-work/search-branches?search=${encodeURIComponent(searchQuery)}`);
      if (response.success) {
        setSearchResults(response.data);
      }
    } catch (error) {
      console.error('Failed to search branches:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleRequestJoin = async (branchId) => {
    try {
      const response = await fetchWithAuth('/api/dentist-work/request', {
        method: 'POST',
        body: JSON.stringify({ branch_id: branchId })
      });

      if (response.success) {
        alert(print_lang('request_sent', 'Request sent successfully'));
        setShowSearchModal(false);
        setSearchQuery('');
        setSearchResults([]);
        loadMyBranches();
      }
    } catch (error) {
      console.error('Failed to request join:', error);
      alert(error.message || print_lang('failed_to_send_request', 'Failed to send request'));
    }
  };

  const handleCancelRequest = async (workId) => {
    if (!window.confirm(print_lang('confirm_cancel_request', 'Are you sure you want to cancel this request?'))) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/dentist-work/request/${workId}`, {
        method: 'DELETE'
      });

      if (response.success) {
        loadMyBranches();
      }
    } catch (error) {
      console.error('Failed to cancel request:', error);
      alert(print_lang('failed_to_cancel', 'Failed to cancel request'));
    }
  };

  const handleLeaveBranch = async (workId, branchName) => {
    if (!window.confirm(print_lang('confirm_leave_branch', `Are you sure you want to leave ${branchName}?`))) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/dentist-work/leave/${workId}`, {
        method: 'DELETE'
      });

      if (response.success) {
        loadMyBranches();
      }
    } catch (error) {
      console.error('Failed to leave branch:', error);
      alert(print_lang('failed_to_leave', 'Failed to leave branch'));
    }
  };

  // Separate active and pending branches
  const activeBranches = myBranches.filter(b => b.status === 'active' && b.request_status !== 'pending');
  const pendingBranches = myBranches.filter(b => b.request_status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {print_lang('working_branches', 'Working Branches')}
            </h2>
            <p className="text-gray-500 mt-1">
              {print_lang('manage_branch_assignments', 'Manage your branch assignments')}
            </p>
          </div>
          <button
            onClick={() => setShowSearchModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {print_lang('request_to_join', 'Request to Join')}
          </button>
        </div>
      </div>

      {/* Active Branches */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            {print_lang('active_branches', 'Active Branches')} ({activeBranches.length})
          </h3>
        </div>

        {activeBranches.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{print_lang('no_active_branches', 'No active branches')}</p>
            <button
              onClick={() => setShowSearchModal(true)}
              className="mt-4 text-blue-600 hover:text-blue-700"
            >
              {print_lang('find_branches', 'Find branches to join')}
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {activeBranches.map(branch => (
              <div key={branch.work_id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{branch.branch_name}</p>
                      <p className="text-sm text-gray-500">{branch.admin_name}</p>
                      {branch.address && (
                        <p className="flex items-center gap-1 mt-1 text-sm text-gray-400">
                          <MapPin className="w-3 h-3" />
                          {branch.address}
                        </p>
                      )}
                      {branch.branch_phone && (
                        <p className="flex items-center gap-1 text-sm text-gray-400">
                          <Phone className="w-3 h-3" />
                          {branch.branch_phone}
                        </p>
                      )}
                      {branch.df_percentage && (
                        <span className="inline-block mt-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          DF: {branch.df_percentage}%
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleLeaveBranch(branch.work_id, branch.branch_name)}
                    className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                  >
                    <LogOut className="w-4 h-4" />
                    {print_lang('leave', 'Leave')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Requests */}
      {pendingBranches.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              {print_lang('pending_requests', 'Pending Requests')} ({pendingBranches.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {pendingBranches.map(branch => (
              <div key={branch.work_id} className="p-4 bg-yellow-50/50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{branch.branch_name}</p>
                      <p className="text-sm text-gray-500">{branch.admin_name}</p>
                      <span className="inline-block mt-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                        {print_lang('request_pending', 'Pending')}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCancelRequest(branch.work_id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                  >
                    <XCircle className="w-4 h-4" />
                    {print_lang('cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 animate-slideUp max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {print_lang('search_branches', 'Search Branches')}
              </h3>
              <button
                onClick={() => {
                  setShowSearchModal(false);
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder={print_lang('search_by_name', 'Search by name or address...')}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={searching}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {searching ? <Loader className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              {searchResults.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {searchQuery ? (
                    <>
                      <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>{print_lang('no_branches_found', 'No branches found')}</p>
                    </>
                  ) : (
                    <>
                      <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>{print_lang('search_to_find', 'Search to find branches')}</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {searchResults.map(branch => (
                    <div key={branch.branch_id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-800">{branch.name}</p>
                          <p className="text-sm text-gray-500">{branch.admin_name}</p>
                          {branch.address && (
                            <p className="flex items-center gap-1 mt-1 text-sm text-gray-400">
                              <MapPin className="w-3 h-3" />
                              {branch.address}
                            </p>
                          )}
                        </div>
                        <div>
                          {branch.already_working ? (
                            <span className="px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg">
                              {print_lang('already_working', 'Already Working')}
                            </span>
                          ) : branch.pending_request ? (
                            <span className="px-3 py-1.5 bg-yellow-100 text-yellow-700 text-sm rounded-lg">
                              {print_lang('request_pending', 'Pending')}
                            </span>
                          ) : (
                            <button
                              onClick={() => handleRequestJoin(branch.branch_id)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                              <Plus className="w-4 h-4" />
                              {print_lang('request_to_join', 'Request')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkingBranches;
